<script>
  // ── Eine Zutat berichtigen: umbenennen und archivieren (Review 5, E-11) ────
  //
  // WARUM ES DAS GEBEN MUSS
  // A6 hat die Verwaltungs-UI bewusst ausgeschlossen („später, eigenes
  // Vorhaben") und dem Datenmodell nur aufgetragen, sie zuzulassen. Das tut es
  // seither: `archiviert_at` steht, der Teilindex gibt archivierte Namen wieder
  // frei, `on delete restrict` schützt die Historie.
  //
  // Inzwischen ist das Lager aber zur faktischen ANLAGE-Oberfläche des Stamms
  // geworden — Wareneingang und Abschluss-Maske legen Zutaten an. Was fehlte,
  // war die Gegenrichtung: Ein Vertipper beim Wareneingang erzeugte eine
  // Stammzutat, die dauerhaft in Suche und Bestand stand und nur über die
  // Konsole wegzubekommen war. Solange der Stamm bei den Testern leer war, war
  // das folgenlos; ab dem zweiten Tester ist es der erste Supportfall.
  //
  // ZWEI HANDLUNGEN, BEWUSST NICHT DREI
  // Umbenennen und Archivieren. **Zusammenführen bleibt draußen** — es braucht
  // eine Entscheidung darüber, was mit den Buchungen des unterlegenen Eintrags
  // geschieht, und die ist nicht nebenbei zu treffen. Sie gehört in die
  // Verwaltungs-UI, die A6 gemeint hat.
  //
  // WAS UMBENENNEN NICHT TUT
  // Es fasst kein Rezept an und verschiebt keine Statistik. Die Auswertung
  // gruppiert über den Katalog-Anzeigenamen, nicht über die Stamm-Kennung
  // (Review 5, E-12) — der neue Name gilt für Lager, Vorschläge und künftige
  // Buchungen. Das steht auch so unter dem Feld, weil die Erwartung sonst eine
  // andere wäre.
  //
  // ARCHIVIEREN NUR BEI BESTAND 0
  // Eine archivierte Zutat fällt aus `zutatStammAktiv()` und damit aus Suche
  // und Vorschlägen. Stünde noch eine Menge darauf, wäre sie eingesperrt:
  // sichtbar in der Liste (die archivierte Zeilen erst bei 0 ausblendet), aber
  // weder buchbar noch korrigierbar. Dieselbe Regel gilt seit L10 schon einmal
  // im Modul — „Die Entscheidung stillzulegen darf keine Zahl einsperren"
  // (lagerZutatSuche). Eine zweite Auslegung derselben Frage wäre eine zu viel.
  import { untrack } from 'svelte';

  /**
   * `zutatId` statt der Zeile selbst — dasselbe Muster wie nebenan in
   * `RichtigstellenSheet.svelte`. Zwei Sheets im selben Ordner, die ihre Zutat
   * verschieden hereinbekommen, wären eine Sache zu viel zum Nachschlagen.
   * @type {{ zutatId: string, bestand: number,
   *          onschliessen: () => void, onarchiviert: () => void }}
   */
  let { zutatId, bestand, onschliessen, onarchiviert } = $props();

  const t = (k, p) => window.t(k, p);

  /**
   * Der Stand beim ÖFFNEN, bewusst als einfaches `const` und nicht als
   * `$derived`.
   *
   * Dieses Sheet lebt genau so lange wie sein Vorgang: Der Aufrufer rendert es
   * hinter `{#if}` und nimmt es beim Schließen wieder heraus. Was beim Öffnen
   * dastand, IST also der richtige Ausgangswert — es gibt kein „später", in dem
   * er veralten könnte.
   *
   * Als reaktive Größe wäre es sogar falsch: Das Feld unten trägt eine laufende
   * Eingabe. Zöge der Spiegel während des Tippens nach (ein Cloud-Abgleich
   * genügt), überschriebe er sie.
   *
   * `untrack` steht deshalb nicht dekorativ da. Svelte 5 warnt bei jedem Lesen
   * einer Prop im Komponentenrumpf (`state_referenced_locally`), weil das in den
   * meisten Fällen ein Versehen ist — hier ist es die Absicht, und `untrack`
   * ist die Art, sie hinzuschreiben, statt die Warnung stehen zu lassen.
   */
  const start = untrack(() => window.zutatById(zutatId));

  let name   = $state(start?.name ?? '');
  let laeuft = $state(false);
  let fehler = $state(/** @type {string|null} */ (null));

  let geaendert = $derived(name.trim() !== '' && name.trim() !== (start?.name ?? ''));
  /** Archivieren nur, wenn nichts eingesperrt wird. */
  let archivierbar = $derived((bestand ?? 0) === 0);

  async function speichern() {
    const neu = name.trim();
    if (!neu) { fehler = t('lager_zutat_fehler_leer'); return; }
    laeuft = true; fehler = null;
    // `zutatAendern` meldet nur wahr/falsch. Die Dublette ist der einzige
    // Fehlschlag, den der Nutzer selbst beheben kann — deshalb wird sie hier
    // vorab erkannt, statt sie in „ließ sich nicht speichern" zu verstecken.
    const kollision = window.zutatFindeNachName(neu);
    if (kollision && kollision.id !== zutatId) {
      laeuft = false; fehler = t('lager_zutat_fehler_dublette'); return;
    }
    const ok = await window.zutatAendern(zutatId, { name: neu });
    laeuft = false;
    if (!ok) { fehler = t('lager_zutat_fehler_speichern'); return; }
    window.notify?.({ level: 'ok', text: t('lager_zutat_gespeichert', { name: neu }) });
    onschliessen();
  }

  async function archivieren() {
    if (!archivierbar) return;
    laeuft = true; fehler = null;
    const ok = await window.zutatArchivieren(zutatId);
    laeuft = false;
    if (!ok) { fehler = t('lager_zutat_fehler_archiv'); return; }
    window.notify?.({ level: 'ok', text: t('lager_zutat_archiviert', { name: start?.name ?? '' }) });
    onarchiviert();
  }
</script>

<div class="lg-host" data-zutat-sheet>
  <button type="button" class="lg-scrim" onclick={onschliessen}
          aria-label={t('lager_sheet_schliessen')}></button>

  <div class="lg-sheet" role="dialog" aria-modal="true">
    <div class="lg-sheet__head">
      <div class="lg-sheet__kopftext">
        <div class="lg-sheet__titel">{t('lager_zutat_sheet_titel')}</div>
        <div class="lg-sheet__unter">{start?.name}</div>
      </div>
      <button type="button" class="lg-sheet__x" onclick={onschliessen}
              aria-label={t('lager_sheet_schliessen')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
             stroke-linecap="round" width="19" height="19" aria-hidden="true">
          <line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/>
        </svg>
      </button>
    </div>

    <div class="lg-zs__feld">
      <label class="lg-zs__label" for="lg-zs-name">{t('lager_zutat_name')}</label>
      <input id="lg-zs-name" class="lg-zs__input" type="text" bind:value={name}
             autocomplete="off" spellcheck="false"
             onkeydown={(e) => { if (e.key === 'Enter' && geaendert) speichern(); }}>
      <p class="lg-zs__hinweis">{t('lager_zutat_name_hinweis')}</p>
      {#if fehler}<p class="lg-zs__fehler" role="alert">{fehler}</p>{/if}
      <button type="button" class="lg-zs__save" disabled={!geaendert || laeuft}
              onclick={speichern}>
        {t('lager_zutat_speichern')}
      </button>
    </div>

    <div class="lg-sheet__fuss">
      <div class="lg-zs__archiv">
        <div class="lg-zs__archivtitel">{t('lager_zutat_archiv_titel')}</div>
        <p class="lg-sheet__hinweis">{t('lager_zutat_archiv_text')}</p>
        {#if archivierbar}
          <button type="button" class="lg-sheet__weg" disabled={laeuft} onclick={archivieren}>
            {t('lager_zutat_archiv_knopf')}
          </button>
        {:else}
          <p class="lg-sheet__hinweis is-sperre">{t('lager_zutat_archiv_sperre')}</p>
        {/if}
      </div>
    </div>
  </div>
</div>

<style>
  /* Dieselbe Hülle wie BuchungSheet und RichtigstellenSheet — schmal eine
     Fläche am unteren Rand, ab 600 px ein Dialog. Ein drittes Overlay-Muster
     im selben Modul wäre eine dritte Sache zum Verstehen. */
  .lg-host { position: fixed; inset: 0; z-index: 60; display: flex; container-type: inline-size; }
  .lg-scrim { position: absolute; inset: 0; background: rgba(41, 37, 36, 0.30); border: 0; padding: 0; }

  .lg-sheet {
    position: relative;
    margin-top: auto;
    width: 100%;
    background: var(--lg-card);
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -12px 40px -18px rgba(28, 25, 23, 0.45);
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  .lg-sheet__head {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 18px 18px 12px;
    border-bottom: 1px solid var(--lg-linie);
  }
  .lg-sheet__kopftext { flex: 1; min-width: 0; }
  .lg-sheet__titel { font-weight: 650; font-size: 1.02rem; color: var(--lg-text); }
  .lg-sheet__unter {
    margin-top: 2px; font-size: 0.84rem; color: var(--lg-text-leise);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .lg-sheet__x {
    flex: none; width: 34px; height: 34px; display: grid; place-items: center;
    border: 0; background: none; color: var(--lg-text-leise); cursor: pointer; border-radius: 8px;
  }
  .lg-sheet__x:hover { background: var(--lg-flaeche); color: var(--lg-text); }

  .lg-zs__feld { padding: 16px 18px 4px; }
  .lg-zs__label {
    display: block; font-size: 0.78rem; font-weight: 600;
    color: var(--lg-text-leise); margin-bottom: 6px;
  }
  .lg-zs__input {
    width: 100%; box-sizing: border-box;
    padding: 10px 12px; font: inherit; color: var(--lg-text);
    background: var(--lg-feld); border: 1px solid var(--lg-linie); border-radius: 10px;
  }
  .lg-zs__input:focus-visible { outline: 2px solid var(--lg-akzent); outline-offset: 1px; }
  .lg-zs__hinweis {
    margin: 8px 0 0; font-size: 0.78rem; line-height: 1.45; color: var(--lg-text-leise);
  }
  .lg-zs__fehler { margin: 8px 0 0; font-size: 0.82rem; color: var(--lg-fehl); }
  .lg-zs__save {
    margin-top: 12px; width: 100%;
    padding: 11px 14px; font: inherit; font-weight: 600;
    color: #fff; background: var(--lg-akzent);
    border: 0; border-radius: 10px; cursor: pointer;
  }
  .lg-zs__save:disabled { opacity: 0.45; cursor: default; }

  .lg-sheet__fuss { padding: 8px 18px 18px; }
  .lg-zs__archiv { border-top: 1px solid var(--lg-linie); padding-top: 14px; }
  .lg-zs__archivtitel {
    font-size: 0.82rem; font-weight: 650; color: var(--lg-text); margin-bottom: 4px;
  }
  .lg-sheet__hinweis {
    margin: 0 0 10px; font-size: 0.8rem; line-height: 1.45; color: var(--lg-text-leise);
  }
  .lg-sheet__hinweis.is-sperre { margin-bottom: 0; }
  .lg-sheet__weg {
    width: 100%; padding: 10px 14px; font: inherit; font-weight: 600;
    color: var(--lg-fehl); background: none;
    border: 1px solid var(--lg-linie); border-radius: 10px; cursor: pointer;
  }
  .lg-sheet__weg:hover { background: var(--lg-flaeche); }
  .lg-sheet__weg:disabled { opacity: 0.45; cursor: default; }

  /* Ab 600 px derselbe Dialog wie nebenan: mittig, begrenzt, mit allen Ecken. */
  @container (min-width: 600px) {
    .lg-host { align-items: center; justify-content: center; padding: 24px; }
    .lg-sheet { margin: 0; max-width: 430px; border-radius: 14px; }
  }
</style>
